import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Users, UserPlus, UserCheck, Clock, Grid3X3, List, X, Check, Pencil, Shield, UserMinus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useObjectMembers, useMembershipActions } from '@/lib/hooks/useMembership';
import { useSession } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';
import { toast } from 'sonner';
import type { ObjectMember } from '@sizzlenz/shared';

// --- Types ---

interface MembersTabProps {
  context: Record<string, any>;
}

// --- Sub-components ---

function MemberCard(t0) {
  const $ = _c(17);
  const {
    member,
    onClick
  } = t0;
  let t1;
  if ($[0] !== member.image || $[1] !== member.name) {
    t1 = member.image ? <img src={member.image} alt={member.name} className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center"><Users className="w-6 h-6 text-gray-400" /></div>;
    $[0] = member.image;
    $[1] = member.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== member.name) {
    t2 = <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>;
    $[3] = member.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== member.role) {
    t3 = <p className="text-xs text-gray-500 capitalize">{member.role}</p>;
    $[5] = member.role;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== member.bio) {
    t4 = member.bio && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{member.bio}</p>;
    $[7] = member.bio;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="text-center min-w-0 w-full">{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== onClick || $[14] !== t1 || $[15] !== t5) {
    t6 = <div className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors bg-white cursor-pointer" onClick={onClick}>{t1}{t5}</div>;
    $[13] = onClick;
    $[14] = t1;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function MemberListItem(t0) {
  const $ = _c(17);
  const {
    member,
    onClick
  } = t0;
  let t1;
  if ($[0] !== member.image || $[1] !== member.name) {
    t1 = member.image ? <img src={member.image} alt={member.name} className="w-10 h-10 rounded-full object-cover shrink-0" /> : <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-gray-400" /></div>;
    $[0] = member.image;
    $[1] = member.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== member.name) {
    t2 = <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>;
    $[3] = member.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== member.role) {
    t3 = <p className="text-xs text-gray-500 capitalize">{member.role}</p>;
    $[5] = member.role;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex-1 min-w-0">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== member.bio) {
    t5 = member.bio && <p className="text-xs text-gray-500 line-clamp-1 max-w-[200px] text-right">{member.bio}</p>;
    $[10] = member.bio;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== onClick || $[13] !== t1 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={onClick}>{t1}{t4}{t5}</div>;
    $[12] = onClick;
    $[13] = t1;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function MemberDetailDialog({
  member,
  open,
  onOpenChange,
  isCurrentUser,
  objectType,
  objectId,
  onBioUpdated,
  isPlatformAdmin
}: {
  member: ObjectMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isCurrentUser: boolean;
  objectType: string;
  objectId: number;
  onBioUpdated?: (newBio: string) => void;
  isPlatformAdmin?: boolean;
}) {
  const maxIntroLength = 200;
  const [isEditing, setIsEditing] = useState(false);
  const [bioValue, setBioValue] = useState('');
  const {
    updateBio,
    remove,
    changeRole
  } = useMembershipActions(objectType, objectId);
  const handleEditClick = () => {
    setBioValue(member?.bio || '');
    setIsEditing(true);
  };
  const handleSave = async () => {
    try {
      await updateBio.mutateAsync(bioValue);
      onBioUpdated?.(bioValue);
      toast.success('Bio updated');
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update bio');
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setBioValue('');
  };

  // Reset local state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setIsEditing(false);
      setBioValue('');
    }
    onOpenChange(newOpen);
  };
  if (!member) return null;
  const shownBio = member.bio;
  const joinedDate = member.joinedAt ? new Date(member.joinedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : null;
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="sr-only">Member Details</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2 shrink-0">
          {member.image ? <img src={member.image} alt={member.name} className="w-20 h-20 rounded-full object-cover" /> : <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">
              <Users className="w-8 h-8 text-gray-400" />
            </div>}
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">{member.name}</p>
            <p className="text-sm text-gray-500 capitalize">{member.role}</p>
          </div>
        </div>
        <div className="flex flex-col items-center gap-4 overflow-y-auto min-h-0">
          {/* Bio section */}
          {isEditing ? <div className="w-full space-y-2">
              <Textarea value={bioValue} onChange={e => setBioValue(e.target.value.slice(0, maxIntroLength))} placeholder="Write a short intro about yourself..." className="resize-none" rows={4} maxLength={maxIntroLength} />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {bioValue.length}/{maxIntroLength}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCancel} className="cursor-pointer">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={updateBio.isPending} className="cursor-pointer">
                    {updateBio.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div> : <div className="w-full">
              {shownBio ? <p className="text-sm text-gray-700 text-center whitespace-pre-wrap">{shownBio}</p> : isCurrentUser ? <p className="text-sm text-gray-400 text-center italic">
                  No intro yet. Add one to tell others about yourself.
                </p> : <p className="text-sm text-gray-400 text-center italic">No intro added yet.</p>}
              {isCurrentUser && <div className="flex justify-center mt-3">
                  <Button variant="outline" size="sm" onClick={handleEditClick} className="cursor-pointer">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    {shownBio ? 'Edit intro' : 'Add intro'}
                  </Button>
                </div>}
            </div>}

          {/* Joined date */}
          {joinedDate && <p className="text-xs text-gray-400">Joined {joinedDate}</p>}

          {/* Platform admin panel */}
          {isPlatformAdmin && <div className="w-full mt-2 pt-3 border-t border-gray-200 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Admin Panel
                </span>
              </div>

              {/* Email */}
              {member.email && <div className="text-sm text-gray-600">
                  <span className="text-gray-400 text-xs">Email:</span> <span>{member.email}</span>
                </div>}

              {/* Role change */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Role</span>
                <div className="flex items-center gap-1">
                  <Button variant={member.role === 'member' ? 'default' : 'outline'} size="sm" onClick={() => {
                if (member.role !== 'member') {
                  changeRole.mutateAsync({
                    memberId: member.id,
                    role: 'member'
                  }).then(() => {
                    toast.success('Role changed to member');
                    onOpenChange(false);
                  }).catch((err: any) => toast.error(err?.message || 'Failed to change role'));
                }
              }} disabled={member.role === 'member' || changeRole.isPending} className="cursor-pointer text-xs">
                    Member
                  </Button>
                  <Button variant={member.role === 'admin' ? 'default' : 'outline'} size="sm" onClick={() => {
                if (member.role !== 'admin') {
                  changeRole.mutateAsync({
                    memberId: member.id,
                    role: 'admin'
                  }).then(() => {
                    toast.success('Role changed to admin');
                    onOpenChange(false);
                  }).catch((err_0: any) => toast.error(err_0?.message || 'Failed to change role'));
                }
              }} disabled={member.role === 'admin' || changeRole.isPending} className="cursor-pointer text-xs">
                    Admin
                  </Button>
                </div>
              </div>

              {/* Remove membership */}
              <Button variant="outline" size="sm" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 cursor-pointer" onClick={() => {
            if (confirm('Remove this member from the hub?')) {
              remove.mutateAsync(member.id).then(() => {
                toast.success('Member removed');
                onOpenChange(false);
              }).catch((err_1: any) => toast.error(err_1?.message || 'Failed to remove member'));
            }
          }} disabled={remove.isPending}>
                <UserMinus className="w-3.5 h-3.5 mr-1.5" />
                {remove.isPending ? 'Removing...' : 'Remove from hub'}
              </Button>
            </div>}
        </div>
      </DialogContent>
    </Dialog>;
}
function PendingMemberItem(t0) {
  const $ = _c(23);
  const {
    member,
    onApprove,
    onReject,
    isApproving,
    isRejecting
  } = t0;
  let t1;
  if ($[0] !== member.image || $[1] !== member.name) {
    t1 = member.image ? <img src={member.image} alt={member.name} className="w-10 h-10 rounded-full object-cover shrink-0" /> : <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-gray-400" /></div>;
    $[0] = member.image;
    $[1] = member.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== member.name) {
    t2 = <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>;
    $[3] = member.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-xs text-amber-600">Pending approval</p>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <div className="flex-1 min-w-0">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isApproving || isRejecting;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Check className="w-4 h-4" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onApprove || $[10] !== t5) {
    t7 = <Button variant="ghost" size="sm" onClick={onApprove} disabled={t5} className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 cursor-pointer" title="Approve">{t6}</Button>;
    $[9] = onApprove;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = isApproving || isRejecting;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <X className="w-4 h-4" />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== onReject || $[14] !== t8) {
    t10 = <Button variant="ghost" size="sm" onClick={onReject} disabled={t8} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer" title="Reject">{t9}</Button>;
    $[13] = onReject;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7) {
    t11 = <div className="flex items-center gap-1">{t7}{t10}</div>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t1 || $[20] !== t11 || $[21] !== t4) {
    t12 = <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/50">{t1}{t4}{t11}</div>;
    $[19] = t1;
    $[20] = t11;
    $[21] = t4;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function JoinButton(t0) {
  const $ = _c(17);
  const {
    objectType,
    objectId,
    currentStatus,
    joinLabel,
    leaveLabel
  } = t0;
  const {
    data: session
  } = useSession();
  const {
    showSignInModal
  } = useSignInModal();
  const {
    join,
    leave
  } = useMembershipActions(objectType, objectId);
  let t1;
  if ($[0] !== join || $[1] !== session?.user || $[2] !== showSignInModal) {
    t1 = async () => {
      if (!session?.user) {
        showSignInModal({
          description: <div className="font-bold p-6"><div className="text-lg">Sign in to join this hub</div></div>,
          preferFullscreenForNewUsers: false,
          onSuccess: () => {
            join.mutateAsync().then(_temp4).catch(_temp5);
          }
        });
        return;
      }
      ;
      try {
        const result_0 = await join.mutateAsync();
        toast.success(result_0.message);
      } catch (t2) {
        const error_0 = t2;
        toast.error(error_0?.message || "Failed to join");
      }
    };
    $[0] = join;
    $[1] = session?.user;
    $[2] = showSignInModal;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleJoin = t1;
  let t2;
  if ($[4] !== leave) {
    t2 = async () => {
      ;
      try {
        await leave.mutateAsync();
        toast.success("You have left the group");
      } catch (t3) {
        const error_1 = t3;
        toast.error(error_1?.message || "Failed to leave");
      }
    };
    $[4] = leave;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleLeave = t2;
  if (currentStatus === "active") {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <UserCheck className="w-4 h-4 mr-1.5" />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    const t4 = leave.isPending ? "Leaving..." : leaveLabel || "Leave";
    let t5;
    if ($[7] !== handleLeave || $[8] !== leave.isPending || $[9] !== t4) {
      t5 = <Button variant="outline" size="sm" onClick={handleLeave} disabled={leave.isPending} className="cursor-pointer">{t3}{t4}</Button>;
      $[7] = handleLeave;
      $[8] = leave.isPending;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (currentStatus === "pending") {
    let t3;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Button variant="outline" size="sm" disabled={true} className="opacity-70"><Clock className="w-4 h-4 mr-1.5" />Pending Approval</Button>;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <UserPlus className="w-4 h-4 mr-1.5" />;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = join.isPending ? "Joining..." : joinLabel || "Join";
  let t5;
  if ($[13] !== handleJoin || $[14] !== join.isPending || $[15] !== t4) {
    t5 = <Button size="sm" onClick={handleJoin} disabled={join.isPending} className="cursor-pointer">{t3}{t4}</Button>;
    $[13] = handleJoin;
    $[14] = join.isPending;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

// --- Main component ---
function _temp5(error) {
  toast.error(error?.message || "Failed to join");
}
function _temp4(result) {
  toast.success(result.message);
}
export function MembersTab({
  context
}: MembersTabProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [allMembers, setAllMembers] = useState<ObjectMember[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ObjectMember | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const {
    data: session
  } = useSession();
  const objectType = context.membershipObjectType || 'content';
  const objectId = context.membershipObjectId || context.hubId;
  const isAdmin = context.membershipIsAdmin === true;
  const {
    data,
    isLoading,
    error
  } = useObjectMembers(objectType, objectId, {
    status: isAdmin ? 'active,pending' : 'active',
    enabled: !!objectId
  });
  const {
    approve,
    reject
  } = useMembershipActions(objectType, objectId);

  // Sync first page into local accumulated state
  const members = (data?.members || []).filter(m => m.status === 'active');
  const pendingMembers = isAdmin ? (data?.members || []).filter(m_0 => m_0.status === 'pending') : [];
  const displayMembers = allMembers.length > 0 ? allMembers : members;
  const totalCount = data?.totalCount || 0;
  const currentUserMembership = data?.currentUserMembership;
  const settings = data?.settings;
  const hasNextPage = allMembers.length > 0 ? !!nextCursor : data?.pagination?.hasNextPage ?? false;

  // Reset accumulated state when initial data changes (e.g. after join/leave)
  const dataRef = useState({
    initialized: false
  })[0];
  if (data && !dataRef.initialized) {
    dataRef.initialized = true;
  }
  const handleMemberClick = (member: ObjectMember) => {
    setSelectedMember(member);
    setDetailOpen(true);
  };
  const handleLoadMore = async () => {
    const cursor = allMembers.length > 0 ? nextCursor : data?.pagination?.nextCursor;
    if (!cursor) return;
    setIsLoadingMore(true);
    try {
      const {
        membershipApi
      } = await import('@/lib/hooks/useMembership');
      const nextPage = await membershipApi.getMembers(objectType, objectId, {
        cursor
      });
      const currentMembers = allMembers.length > 0 ? allMembers : members;
      setAllMembers([...currentMembers, ...nextPage.members]);
      setNextCursor(nextPage.pagination.nextCursor);
    } catch (e) {
      // Silently fail, user can retry
    } finally {
      setIsLoadingMore(false);
    }
  };
  const handleApprove = async (memberId: number) => {
    try {
      await approve.mutateAsync(memberId);
      toast.success('Member approved');
    } catch (error_0: any) {
      toast.error(error_0?.message || 'Failed to approve');
    }
  };
  const handleReject = async (memberId_0: number) => {
    try {
      await reject.mutateAsync(memberId_0);
      toast.success('Member rejected');
    } catch (error_1: any) {
      toast.error(error_1?.message || 'Failed to reject');
    }
  };

  // Check if current user is a member (for "Edit my intro" button)
  const currentUserId = session?.user?.id ? Number(session.user.id) : null;
  const isCurrentUserMember = !!currentUserMembership && currentUserMembership.status === 'active';

  // Find the current user's member entry from the fresh query data for the detail dialog
  const currentUserMemberEntry = currentUserId ? (data?.members || []).find(m_1 => m_1.userId === currentUserId && m_1.status === 'active') : null;
  const handleEditMyIntro = () => {
    if (currentUserMemberEntry) {
      setSelectedMember(currentUserMemberEntry);
      setDetailOpen(true);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-6 h-6 border-2 border-primary-300 border-t-primary-600 rounded-full" />
      </div>;
  }
  if (error) {
    // If membership isn't enabled (404), show a friendly empty state instead of error
    const errorStatus = (error as any)?.response?.status;
    if (errorStatus === 404) {
      return <div className="text-center py-12 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Membership is not enabled for this hub</p>
        </div>;
    }
    return <div className="text-center py-12 text-gray-500">
        <p>Unable to load members</p>
      </div>;
  }
  return <div className="max-w-[800px] mx-auto space-y-4">
      {/* Header with count, join button, and view toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {settings?.showMemberCount !== false && <span className="text-sm text-gray-500">
              {totalCount} {totalCount === 1 ? 'member' : 'members'}
              {settings?.capacity ? ` / ${settings.capacity}` : ''}
            </span>}
        </div>

        <div className="flex items-center gap-2">
          {isCurrentUserMember && <Button variant="outline" size="sm" onClick={handleEditMyIntro} className="cursor-pointer">
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              Edit my hub profile
            </Button>}

          <JoinButton objectType={objectType} objectId={objectId} currentStatus={currentUserMembership?.status || null} joinLabel={settings?.joinLabel} leaveLabel={settings?.leaveLabel} />

          {/* View toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('grid')} className={cn('p-1.5 cursor-pointer transition-colors', viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600')} title="Grid view">
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={cn('p-1.5 cursor-pointer transition-colors', viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600')} title="List view">
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Pending members (admin only) */}
      {isAdmin && pendingMembers.length > 0 && <div className="space-y-2">
          <h3 className="text-sm font-medium text-amber-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            Pending Approval ({pendingMembers.length})
          </h3>
          <div className="space-y-1">
            {pendingMembers.map(member_0 => <PendingMemberItem key={member_0.id} member={member_0} onApprove={() => handleApprove(member_0.id)} onReject={() => handleReject(member_0.id)} isApproving={approve.isPending} isRejecting={reject.isPending} />)}
          </div>
        </div>}

      {/* Members list/grid */}
      {displayMembers.length === 0 ? <div className="text-center py-12 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No members yet</p>
          <p className="text-xs mt-1">Be the first to join!</p>
        </div> : viewMode === 'grid' ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {displayMembers.map(member_1 => <MemberCard key={member_1.id} member={member_1} onClick={() => handleMemberClick(member_1)} />)}
        </div> : <div className="divide-y divide-gray-100">
          {displayMembers.map(member_2 => <MemberListItem key={member_2.id} member={member_2} onClick={() => handleMemberClick(member_2)} />)}
        </div>}

      {/* Load More */}
      {hasNextPage && <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={isLoadingMore} className="cursor-pointer">
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>}

      {/* Member Detail Dialog */}
      <MemberDetailDialog member={selectedMember} open={detailOpen} onOpenChange={setDetailOpen} isCurrentUser={!!currentUserId && selectedMember?.userId === currentUserId} objectType={objectType} objectId={objectId} isPlatformAdmin={session?.user?.isAdmin === true} onBioUpdated={newBio => {
      if (selectedMember) {
        setSelectedMember({
          ...selectedMember,
          bio: newBio
        });
      }
    }} />
    </div>;
}